import { useEffect, useState } from 'react'

/**
 * Detects the pointer release that ends a native HTML5 drag, without waiting
 * for react-dnd to end the drag.
 *
 * react-dnd clears its dragging state on `drop` or on `dragend`, and `dragend`
 * can arrive late or never: the browser dispatches it at the drag source node,
 * while the HTML5 backend listens for it on `window` only. A source unmounted
 * mid-drag — spring-loaded drop targets reload the explorer panes under the
 * pointer — leaves no propagation path to `window`, so the drag is ended only
 * by the backend's `mousemove` fallback, i.e. when the user next moves the
 * mouse. That is what leaves the drag preview floating after the drop.
 *
 * The release itself is observable straight away: the drag-and-drop model fires
 * `drop` at the target, or `dragleave` at the last target when the drag is
 * cancelled, both before `dragend`. `dragleave` also fires when the pointer
 * crosses between elements, but a `dragenter`/`dragover` for the next element
 * then follows before the next paint — so a release is a `dragleave` that
 * nothing follows.
 *
 * A pointer leaving the browser window mid-drag reports as released too: the
 * drag is still live, but no offsets arrive while the pointer is outside, so a
 * preview drawn there would be frozen at the window edge anyway. Re-entering
 * the document reports not-released again.
 *
 * @param isDragging - Whether react-dnd reports a drag in progress
 * @returns Whether the pointer has been released for the current drag
 */
function useDragReleased(isDragging: boolean): boolean {
  const [released, setReleased] = useState(false)

  useEffect(() => {
    if (!isDragging) {
      setReleased(false)
      return
    }

    let pendingRelease: number | null = null

    const cancelPendingRelease = () => {
      if (pendingRelease !== null) {
        cancelAnimationFrame(pendingRelease)
        pendingRelease = null
      }
    }

    const release = () => {
      cancelPendingRelease()
      setReleased(true)
    }

    const keepDragging = () => {
      cancelPendingRelease()
      setReleased(false)
    }

    const releaseUnlessPointerReturns = () => {
      cancelPendingRelease()
      pendingRelease = requestAnimationFrame(release)
    }

    window.addEventListener('dragleave', releaseUnlessPointerReturns, true)
    window.addEventListener('dragenter', keepDragging, true)
    window.addEventListener('dragover', keepDragging, true)
    window.addEventListener('drop', release, true)

    return () => {
      cancelPendingRelease()
      window.removeEventListener('dragleave', releaseUnlessPointerReturns, true)
      window.removeEventListener('dragenter', keepDragging, true)
      window.removeEventListener('dragover', keepDragging, true)
      window.removeEventListener('drop', release, true)
    }
  }, [isDragging])

  return released
}

export default useDragReleased
